import moment from 'moment-timezone';

import { TASK_DISPLAY_STATUS } from '../../constants';
import {
  daysUntil,
  getAppointmentReadinessStatus,
  getTaskDisplayStatus,
  getTaskReadinessStatus,
  isTaskReadinessComplete,
} from '../../helpers';
import { visitCode, visitLabelWithoutCode } from '../Visit';

const COMPLETED_VISIT_STATUSES = ['3', '4'];
const CANCELED_VISIT_STATUSES = ['x', 'canceled', 'cancelled'];
const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

const dateOnly = (value, timeZone) => {
  if (!value) return '';
  const parsed = timeZone ? moment.tz(value, timeZone) : moment(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const clockOnly = (value, timeZone) => {
  if (!value) return '';
  const parsed = timeZone ? moment.tz(value, timeZone) : moment(value);
  return parsed.isValid() ? parsed.format('HH:mm') : '';
};

const toVisit = (visit) => ({
  id: visit.id,
  date: dateOnly(visit.startTime, visit.timezone),
  time: clockOnly(visit.startTime, visit.timezone),
  timeZone: visit.timezone,
  visitType: visit.appointmentType,
  label: visitLabelWithoutCode(visit.appointmentType) || 'Visit',
  code: visitCode({ visitType: visit.appointmentType }),
  clinician: visit.providerName,
  location: visit.location,
  statusRaw: `${visit.statusRaw}`.toLowerCase(),
});

export const isDoneTask = (task) => getTaskDisplayStatus(task) === TASK_DISPLAY_STATUS.Done;

export const taskDoneDate = (task) => dateOnly(task.updatedAt);

export const compareOpenTasks = (a, b) => {
  const byDue = daysUntil(a.due) - daysUntil(b.due);
  if (byDue) return byDue;
  return (PRIORITY_RANK[a.priority] ?? 4) - (PRIORITY_RANK[b.priority] ?? 4);
};

export const buildMemberRecord = ({ patientId, visits = [], tasks = [], today }) => {
  const parentTasks = tasks.filter((task) => !task.isSubtask);
  const doneTasks = parentTasks
    .filter(isDoneTask)
    .sort((a, b) => taskDoneDate(a).localeCompare(taskDoneDate(b)));
  const openTasks = parentTasks.filter((task) => !isDoneTask(task)).sort(compareOpenTasks);

  const mapped = visits.map(toVisit).filter((visit) => visit.date);
  const pastVisits = mapped
    .filter((visit) => COMPLETED_VISIT_STATUSES.includes(visit.statusRaw))
    .sort((a, b) => a.date.localeCompare(b.date));
  const upcomingVisits = mapped
    .filter(
      (visit) =>
        visit.date >= today &&
        !COMPLETED_VISIT_STATUSES.includes(visit.statusRaw) &&
        !CANCELED_VISIT_STATUSES.includes(visit.statusRaw),
    )
    .sort((a, b) => a.date.localeCompare(b.date));

  const episodes = [];
  let previousDate = '';
  pastVisits.forEach((visit) => {
    episodes.push({
      ...visit,
      kind: 'visit',
      tasks: doneTasks.filter((task) => {
        const date = taskDoneDate(task);
        return date > previousDate && date <= visit.date;
      }),
    });
    previousDate = visit.date;
  });

  const sinceLast = doneTasks.filter((task) => taskDoneDate(task) > previousDate);
  if (sinceLast.length) {
    episodes.push({
      id: 'since-last',
      kind: 'between',
      label: 'Earlier work',
      tasks: sinceLast,
    });
  }

  episodes.push({ id: 'now', kind: 'now', date: today });

  const [next, ...later] = upcomingVisits;
  if (openTasks.length) {
    episodes.push({
      id: 'open',
      kind: 'between',
      open: true,
      label: next ? 'Open work before next visit' : 'Open work',
      tasks: openTasks,
    });
  }

  if (next) {
    const visitTasks = parentTasks.filter((task) => `${task.externalObjectId}` === `${next.id}`);
    const status = getAppointmentReadinessStatus(visitTasks);
    episodes.push({
      ...next,
      kind: 'upcoming',
      readinessStatus: status === 'default' ? 'neutral' : status,
      readinessBlocks: visitTasks.map((task) => ({
        id: task.id,
        status: getTaskReadinessStatus(task),
      })),
      readinessDone: visitTasks.filter(isTaskReadinessComplete).length,
      readinessTotal: visitTasks.length,
    });
  }
  later.forEach((visit) => episodes.push({ ...visit, kind: 'future' }));

  return {
    patientId,
    today,
    episodes,
    openTasks,
    doneTasks,
    visitCount: pastVisits.length,
  };
};

export const rankMembersByUrgency = (tasks) => {
  const byPatient = {};
  tasks
    .filter((task) => !task.isSubtask && !isDoneTask(task))
    .forEach((task) => {
      const key = `${task.patientId}`;
      (byPatient[key] ||= { patientId: key, patient: task.patient, tasks: [] }).tasks.push(task);
    });

  return Object.values(byPatient)
    .map((member) => {
      const sorted = [...member.tasks].sort(compareOpenTasks);
      const [mostUrgent] = sorted;
      const overdue = daysUntil(mostUrgent.due) < 0;
      const tier = overdue ? 0 : mostUrgent.priority === 'critical' ? 1 : 2;
      return { ...member, mostUrgent, openCount: sorted.length, tier };
    })
    .sort((a, b) => a.tier - b.tier || daysUntil(a.mostUrgent.due) - daysUntil(b.mostUrgent.due));
};
